import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import GameToolbar from '../../../components/GameToolbar/GameToolbar';
import { GAME_COPY, GAME_KEYS, MATCHSTICK_CORRECT } from '../../../data/games';
import { finishGame } from '../../../utils/gameSave';
import './MatchsticksPage.css';

const CORRECT = new Set(MATCHSTICK_CORRECT);

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function MatchsticksPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const svgHostRef = useRef(null);
  const [svgReady, setSvgReady] = useState(false);
  const [removed, setRemoved] = useState(() => new Set());
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [timeLeft, setTimeLeft] = useState(120);
  const [feedback, setFeedback] = useState('');
  const doneRef = useRef(false);
  const elapsedRef = useRef(0);
  const wrongRef = useRef(0);
  const busyRef = useRef(false);
  const removedRef = useRef(removed);

  useEffect(() => {
    removedRef.current = removed;
  }, [removed]);

  const submit = useCallback(async () => {
    if (doneRef.current || busyRef.current || !user) return;
    doneRef.current = true;
    busyRef.current = true;
    const points = 50 - wrongRef.current;
    try {
      const result = await finishGame({
        user,
        gameKey: GAME_KEYS.matchsticks,
        score: points,
        elapsedSeconds: elapsedRef.current,
      });
      if (result.alreadyPlayed) {
        navigate('/hub');
      } else {
        navigate(
          `/thank-you?game=${GAME_KEYS.matchsticks}&points=${result.coinsEarned ?? 0}`
        );
      }
    } catch (err) {
      console.error(err);
      doneRef.current = false;
      busyRef.current = false;
      setFeedback('Could not save score. Try again.');
    }
  }, [navigate, user]);

  useEffect(() => {
    const tick = setInterval(() => {
      elapsedRef.current += 1;
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(tick);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (timeLeft === 0) submit();
  }, [timeLeft, submit]);

  // Load authentic PONGAL SVG (includes special letter sticks)
  useEffect(() => {
    let cancelled = false;
    fetch('/matchsticks/puzzle.svg')
      .then((r) => r.text())
      .then((markup) => {
        if (cancelled || !svgHostRef.current) return;
        svgHostRef.current.innerHTML = markup;
        const svg = svgHostRef.current.querySelector('svg');
        if (svg) {
          svg.classList.add('match-board');
          svg.setAttribute('role', 'img');
          svg.setAttribute('aria-label', 'Matchstick puzzle');
        }
        setSvgReady(true);
      })
      .catch((err) => {
        console.error('Failed to load matchsticks SVG', err);
        setFeedback('Could not load puzzle.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!svgReady || !svgHostRef.current) return;
    const host = svgHostRef.current;

    const onStickClick = (e) => {
      const el = e.target.closest('.find-stick');
      if (!el || doneRef.current || busyRef.current) return;
      const id = el.id;
      if (!id || removedRef.current.has(id)) return;

      if (CORRECT.has(id)) {
        const next = new Set(removedRef.current);
        next.add(id);
        removedRef.current = next;
        setRemoved(next);
        el.classList.add('removed');
        setFeedback('Nice!');
        if (next.size >= MATCHSTICK_CORRECT.length) {
          setTimeout(() => submit(), 350);
        }
      } else {
        wrongRef.current += 1;
        setWrongAttempts(wrongRef.current);
        el.classList.add('wrong-flash');
        setFeedback('Wrong stick');
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
          navigator.vibrate(40);
        }
        setTimeout(() => el.classList.remove('wrong-flash'), 350);
      }
    };

    host.addEventListener('click', onStickClick);
    return () => host.removeEventListener('click', onStickClick);
  }, [svgReady, submit]);

  const copy = GAME_COPY.matchsticks;

  return (
    <div className="match-page bg-games" style={{ '--page-bg': 'url(/img/games-background.jpg)' }}>
      <GameToolbar rules={copy.rules} />
      <p className="match-title">{copy.title}</p>
      <div className="match-stats">
        <div className="match-pill">Wrong Attempts: {wrongAttempts}</div>
        <div className="match-pill">Time Left: {formatTime(timeLeft)}</div>
      </div>
      <div className="match-feedback">{feedback}</div>
      <div className="match-board-wrap" ref={svgHostRef} aria-busy={!svgReady} />
    </div>
  );
}
